import { useEffect, useMemo, useState } from "react";
import {
  Search,
  Wallet,
  CheckCircle2,
  CreditCard,
  TrendingUp,
  ArrowUpRight,
  Calendar,
} from "lucide-react";
import { api } from "../../lib/auth";

function StatusPill({ status }) {
  const styles = {
    verified:
      "bg-emerald-50 text-emerald-700 border border-emerald-200",
    pending:
      "bg-amber-50 text-amber-700 border border-amber-200",
    failed:
      "bg-rose-50 text-rose-700 border border-rose-200",
  };

  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${
        styles[status] || "bg-slate-100 text-slate-600"
      }`}
    >
      {(status || "").replace("_", " ")}
    </span>
  );
}

function StatCard({ title, value, icon: Icon, tint, hint }) {
  const map = {
    emerald:
      "from-emerald-500/10 to-emerald-100 text-emerald-700",
    blue:
      "from-sky-500/10 to-sky-100 text-sky-700",
    amber:
      "from-amber-500/10 to-amber-100 text-amber-700",
    violet:
      "from-violet-500/10 to-violet-100 text-violet-700",
  };

  return (
    <div className="relative overflow-hidden rounded-3xl border border-black/5 bg-white p-6 shadow-sm">
      <div
        className={`absolute top-0 right-0 w-40 h-40 rounded-full blur-3xl opacity-40 bg-gradient-to-br ${map[tint]}`}
      />

      <div
        className={`relative w-12 h-12 rounded-2xl flex items-center justify-center bg-gradient-to-br ${map[tint]}`}
      >
        <Icon size={22} />
      </div>

      <div className="relative mt-5">
        <div className="text-sm text-black/55 font-medium">
          {title}
        </div>

        <div className="mt-1 text-3xl font-black tracking-tight text-black">
          {value}
        </div>

        <div className="mt-2 text-xs text-black/45">
          {hint}
        </div>
      </div>
    </div>
  );
}

export default function AdminTransactions() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api
      .get("/admin/transactions")
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]));
  }, []);

  const filtered = useMemo(() => {
    return rows.filter((t) => {
      const q = search.toLowerCase();

      return (
        t?.tenant?.toLowerCase()?.includes(q) ||
        t?.reference?.toLowerCase()?.includes(q) ||
        t?.method?.toLowerCase()?.includes(q)
      );
    });
  }, [rows, search]);

  // Stats
  const total = rows.length;

  const success = rows.filter(
    (t) => t.status === "verified"
  ).length;

  const pending = rows.filter(
    (t) => t.status === "pending"
  ).length;

  const volume = rows.reduce(
    (sum, t) => sum + Number(t.amount || 0),
    0
  );

  return (
    <div className="min-h-screen bg-[#F6F8FB]">
      <div className="max-w-[1600px] mx-auto p-6 lg:p-10">

        {/* HERO */}
        <div className="relative overflow-hidden rounded-[32px] border border-black/5 bg-white p-8 lg:p-10 shadow-sm mb-7">
          <div className="absolute top-0 right-0 w-[420px] h-[420px] bg-sky-100 rounded-full blur-3xl opacity-40" />

          <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-50 text-sky-700 text-xs font-bold uppercase tracking-wider mb-5">
                <Wallet size={14} />
                Financial Overview
              </div>

              <h1 className="text-4xl lg:text-5xl font-black tracking-tight text-black leading-tight">
                Transactions Dashboard
              </h1>

              <p className="mt-3 text-black/60 max-w-2xl text-sm lg:text-base">
                Monitor all payment activity, transaction
                volume, billing status, and revenue flow
                across your tenant ecosystem.
              </p>
            </div>

            <div className="flex gap-4 flex-wrap">
              <div className="bg-[#F7F8FA] border border-black/5 rounded-2xl px-5 py-4 min-w-[180px]">
                <div className="text-xs uppercase font-bold text-black/45 tracking-wider">
                  Total Volume
                </div>

                <div className="mt-2 text-2xl font-black text-black">
                  ₦{volume.toLocaleString()}
                </div>
              </div>

              <div className="bg-[#F7F8FA] border border-black/5 rounded-2xl px-5 py-4 min-w-[180px]">
                <div className="text-xs uppercase font-bold text-black/45 tracking-wider">
                  Successful
                </div>

                <div className="mt-2 text-2xl font-black text-emerald-600">
                  {success}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* STATS */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-7">
          <StatCard
            title="Total Transactions"
            value={total}
            icon={CreditCard}
            tint="blue"
            hint="All processed payments"
          />

          <StatCard
            title="Verified Payments"
            value={success}
            icon={CheckCircle2}
            tint="emerald"
            hint="Successfully completed"
          />

          <StatCard
            title="Pending Payments"
            value={pending}
            icon={Calendar}
            tint="amber"
            hint="Awaiting confirmation"
          />

          <StatCard
            title="Transaction Volume"
            value={`₦${volume.toLocaleString()}`}
            icon={TrendingUp}
            tint="violet"
            hint="Total processed value"
          />
        </div>

        {/* TABLE */}
        <div className="rounded-[30px] border border-black/5 bg-white shadow-sm overflow-hidden">

          {/* TOP */}
          <div className="p-6 border-b border-black/5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-black text-black">
                Transaction Records
              </h2>

              <p className="text-sm text-black/50 mt-1">
                View and monitor all tenant payment activity.
              </p>
            </div>

            <div className="relative w-full lg:w-[340px]">
              <Search
                size={18}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-black/35"
              />

              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tenant, reference, method..."
                className="w-full h-12 rounded-2xl border border-black/10 bg-[#F8FAFC] pl-11 pr-4 text-sm outline-none focus:border-sky-500 focus:bg-white transition"
              />
            </div>
          </div>

          {/* TABLE CONTENT */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px]">
              <thead>
                <tr className="bg-[#F8FAFC] border-b border-black/5">
                  {[
                    "Tenant",
                    "Reference",
                    "Method",
                    "Amount",
                    "Status",
                    "Date",
                    "",
                  ].map((h) => (
                    <th
                      key={h}
                      className="text-left px-6 py-4 text-[11px] uppercase tracking-wider font-bold text-black/45"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {filtered.map((t, i) => (
                  <tr
                    key={t.id}
                    className={`border-b border-black/5 hover:bg-[#FAFBFC] transition ${
                      i % 2 === 0 ? "bg-white" : "bg-[#FCFCFD]"
                    }`}
                  >
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 text-white flex items-center justify-center font-black text-sm shadow-sm">
                          {(t.tenant || "T")[0]}
                        </div>

                        <div>
                          <div className="font-bold text-black">
                            {t.tenant}
                          </div>

                          <div className="text-xs text-black/45">
                            Tenant Account
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-5">
                      <div className="inline-flex px-3 py-1 rounded-xl bg-slate-100 text-slate-700 font-mono text-xs">
                        {t.reference}
                      </div>
                    </td>

                    <td className="px-6 py-5">
                      <div className="capitalize text-black/70 font-medium">
                        {(t.method || "").replace("_", " ")}
                      </div>
                    </td>

                    <td className="px-6 py-5">
                      <div className="font-black text-black">
                        ₦
                        {Number(t.amount || 0).toLocaleString()}
                      </div>
                    </td>

                    <td className="px-6 py-5">
                      <StatusPill status={t.status} />
                    </td>

                    <td className="px-6 py-5">
                      <div className="text-sm text-black/55">
                        {(t.date || "").slice(0, 10)}
                      </div>
                    </td>

                    <td className="px-6 py-5 text-right">
                      <button className="inline-flex items-center gap-1 text-sm font-bold text-sky-600 hover:text-sky-700">
                        Details
                        <ArrowUpRight size={15} />
                      </button>
                    </td>
                  </tr>
                ))}

                {filtered.length === 0 && (
                  <tr>
                    <td colSpan="7" className="py-24 text-center">
                      <div className="flex flex-col items-center">
                        <div className="w-20 h-20 rounded-full bg-[#F3F5F7] flex items-center justify-center mb-4">
                          <Wallet
                            size={34}
                            className="text-black/25"
                          />
                        </div>

                        <h3 className="text-xl font-bold text-black">
                          No transactions found
                        </h3>

                        <p className="text-sm text-black/50 mt-2">
                          There are currently no transaction
                          records available.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}